import { IEntity } from '@pf/shared-common';
import {
    CategoryFragment,
    ContainerFragment,
    EquipmentTypeFragment,
    ItemFragment,
    ResourceAuth,
    SlotSearchRequestParams,
    SlotType,
    SlotViewModelCreateBody,
    SlotViewModelRead,
    ZoneFragment,
} from '@control-tower/platform-wms';

import {
    ICustomFieldDto,
    ILocationFragmentDto,
} from '@pf/shared/util-platform';
import { asArraySafe, compareSafe } from '@pf/shared-utility';

export type SlotRead = SlotViewModelRead &
    IEntity & { containers: ContainerFragment[] };
export type SlotCreate = SlotViewModelCreateBody;
export type SlotSearchParams = SlotSearchRequestParams;

export function isTypeNeverExcludedFromOperations(slotType: SlotType): boolean {
    return slotType === SlotType.Primary || slotType === SlotType.Secondary;
}

export function isTypeExcludedFromOperations(slotType?: SlotType): boolean {
    return (
        !slotType ||
        [
            SlotType.Staging as string,
            SlotType.Quarantine as string,
            SlotType.NonCompliance as string,
        ].includes(slotType)
    );
}

export function getDefaultContainer(
    slot: Pick<SlotDto, 'barcode' | 'containers'> | null | undefined
): ContainerFragment | null {
    const containers = asArraySafe(slot?.containers);

    if (containers.length === 0) return null;

    return (
        containers.find(c => compareSafe(c.lpn, slot?.barcode)) ?? containers[0]
    );
}

export class SlotDto implements IEntity {
    constructor(data: Partial<SlotDto>) {
        Object.assign(this, data);
        this.containers = data.containers || [];
    }
    id!: string;
    isDeleted?: boolean;
    createdDate?: string;
    modifiedDate?: string;
    slotStatus?: string | null;
    slotPriority?: number;
    reservedForLocation?: ILocationFragmentDto;
    reservedForItem?: ItemFragment;
    reservedForItemCategory?: CategoryFragment;
    zoneId!: string;
    zone!: ZoneFragment;
    slotType?: SlotType;
    minQuantityOverride?: number;
    maxQuantityOverride?: number;
    replenishFromSlotId?: string;
    barcode?: string | null;
    excludeFromOperations = false;
    containers: ContainerFragment[] = [];
    resourceAuth?: ResourceAuth;
    customFields?: ICustomFieldDto[];
    equipmentTypeId?: string;
    equipmentType?: EquipmentTypeFragment;
}
